// src/components/pages/dashboard/ProfessionalDashboard.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Grid, 
  useMediaQuery, 
  useTheme,
  CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useSubscription } from '../../../contexts/context/SubscriptionContext';

// Importar componentes del dashboard
import PatientSummary from './widgets/PatientSummary';
import AppointmentsWidget from './widgets/AppointmentsWidget';
import TasksWidget from './widgets/TasksWidget';
import PatientWidget from './widgets/PatientWidget';
import QuickNotesWidget from './widgets/QuickNotesWidget';

// Importar sidebar y header compartidos
import Sidebar from '../../common/sidebar/Sidebar';
import DashboardHeader from '../../common/header/DashboardHeader';

// Importar servicio de autenticación
import agthealthService from '../../../services/agthealthService';

// Importar el modal de pacientes
import PatientModal from '../patients/PatientModal';

// Importar componente MyAGTProfile
import MyAGTProfile from '../myAGT/MyAGTProfile';

// Importar componente Intercom
import IntercomDashboard from '../../common/intercom/IntercomDashboard';

// Importar componente Configuration
import Configuration from '../configuration/Configuration';

const DrawerWidth = 240;

const Main = styled(Box)(({ theme, open, isMobile }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  paddingTop: 0, // Quitar padding superior porque el header ya tiene su propio padding
  paddingBottom: theme.spacing(16), // Más espacio inferior (~128px)
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: isMobile ? 0 : `-${DrawerWidth}px`,
  ...(open && {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  }),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
    paddingTop: 0,
    paddingBottom: theme.spacing(14), // También más espacio en móviles (~112px)
  }
}));

const ProfessionalDashboard = ({ user: initialUser, activePage = 'dashboard', onLogout }) => {
  const { isPro, hasSubscription } = useSubscription();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [user, setUser] = useState(initialUser || null);
  const [loading, setLoading] = useState(!initialUser);
  
  // Estado para el modal de pacientes
  const [patientModal, setPatientModal] = useState({
    open: false,
    patientId: null
  });
  
  // Verificar si el usuario tiene acceso al dashboard
  useEffect(() => {
    // Si no tiene suscripción, no debería estar aquí
    if (!isPro && !hasSubscription && !loading) {
      console.log("Dashboard - Usuario sin suscripción intentando acceder");
      // No es necesario redirigir porque DashboardGuard ya se encarga de mostrar el promo
    }
  }, [isPro, hasSubscription, navigate, loading]);
  
  useEffect(() => {
    // Aquí podríamos cargar información del usuario desde la API
    const fetchUserProfile = async () => {
      setLoading(true);
      try {
        const response = await agthealthService.getUserProfile();
        if (response.data) {
          // Asegurémonos de que isIndependent esté establecido correctamente
          const token = localStorage.getItem('agthealth_token');
          let isIndependent = false;
          
          if (token) {
            try {
              const tokenData = JSON.parse(atob(token.split('.')[1]));
              isIndependent = tokenData.role === 'independent_professional';
            } catch (error) {
              console.error('Error al decodificar token:', error);
            }
          }
          
          setUser({
            ...response.data,
            isIndependent: isIndependent
          });
        }
      } catch (error) {
        console.error('Error al cargar perfil de usuario:', error);
      } finally {
        setLoading(false);
      }
    };
    
    if (!initialUser) {
      fetchUserProfile();
    } else {
      // Si tenemos initialUser, asegurémonos de que tiene la propiedad isIndependent
      const token = localStorage.getItem('agthealth_token');
      let isIndependent = false;
      
      if (token) {
        try {
          const tokenData = JSON.parse(atob(token.split('.')[1]));
          isIndependent = tokenData.role === 'independent_professional';
        } catch (error) {
          console.error('Error al decodificar token:', error);
        }
      }
      
      setUser({
        ...initialUser,
        isIndependent: isIndependent
      });
      setLoading(false);
    }
  }, [initialUser]);
  
  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };
  
  const handleLogout = async () => {
    try {
      await agthealthService.logout();
      if (onLogout) {
        onLogout();
      } else {
        navigate('/login');
      }
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
      localStorage.removeItem('agthealth_token');
      navigate('/login');
    }
  };

  const handleSettings = () => {
    navigate('/settings');
  };
  
  // Función para abrir el modal de nuevo paciente
  const handleNewPatient = () => {
    setPatientModal({
      open: true,
      patientId: null
    });
  };

  // Función para cerrar el modal
  const handleClosePatientModal = () => {
    setPatientModal({
      ...patientModal,
      open: false
    });
  };
  
  // Función que se llama cuando se guarda un paciente
  const handlePatientSaved = () => {
    // Aquí podrías actualizar datos del dashboard si es necesario
    // Por ejemplo, recargar el widget de pacientes
    // Este evento será manejado por el PatientWidget directamente
  };

  // Determinar el título según la página activa
  const getPageTitle = () => {
    switch (activePage) {
      case 'my-agt':
        return 'Mi AGT';
      case 'settings':
        return 'Configuración';
      case 'intercom':
        return 'Soporte';
      case 'dashboard':
      default:
        return 'Dashboard';
    }
  };

  return (
    <Box sx={{ display: 'flex' }}>
      {/* Sidebar compartido */}
      <Sidebar
        open={drawerOpen}
        isMobile={isMobile}
        onClose={handleDrawerToggle}
        onNewPatient={handleNewPatient}
        onLogout={handleLogout}
        activeSection={activePage}
        user={user}
      />
      
      {/* Main Content */}
      <Main open={drawerOpen} isMobile={isMobile}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <DashboardHeader 
              title={getPageTitle()}
              user={user} 
              isMobile={isMobile} 
              onMenuClick={handleDrawerToggle}
              onLogout={handleLogout}
              onSettings={handleSettings}
            />
            
            {activePage === 'dashboard' && (
              <Box>
                <PatientSummary />
                
                <Grid container spacing={3} sx={{ mt: 3 }}>
                  <Grid item xs={12} md={6}>
                    <AppointmentsWidget />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <PatientWidget />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <QuickNotesWidget />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TasksWidget />
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Renderizar Mi AGT cuando activePage es 'my-agt' */}
            {activePage === 'my-agt' && (
              <MyAGTProfile user={user} />
            )}

            {/* Renderizar Intercom cuando activePage es 'intercom' */}
            {activePage === 'intercom' && (
              <IntercomDashboard user={user} />
            )}
            
            {/* Renderizar la configuración cuando activePage es 'settings' */}
            {activePage === 'settings' && (
              <Configuration />
            )}
          </>
        )}
      </Main>
      
      {/* Modal de paciente */}
      <PatientModal
        open={patientModal.open}
        onClose={handleClosePatientModal}
        patientId={patientModal.patientId}
        onSaved={handlePatientSaved}
      />
    </Box>
  );
};

export default ProfessionalDashboard;